import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Participant } from '../types';
import { XIcon, BellIcon, SendIcon } from './icons';
import Logo from './Logo';

interface PushNotificationModalProps {
    targetName: string;
    isGroup?: boolean;
    isOpen: boolean;
    onClose: () => void;
    onSend: (title: string, body: string) => Promise<void>;
}

const PushNotificationModal: React.FC<PushNotificationModalProps> = ({ targetName, isGroup, isOpen, onClose, onSend }) => {
    const [title, setTitle] = useState(isGroup ? `Update for ${targetName}` : "Update from Dekko");
    const [body, setBody] = useState(isGroup ? "The research team has sent an update to your group." : "A researcher has sent you a new update. Tap to view within the app.");
    const [isSending, setIsSending] = useState(false);

    if (!isOpen) return null;

    const handleSend = async () => {
        setIsSending(true);
        await onSend(title, body);
        setIsSending(false);
        onClose();
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                {/* Header */}
                <div className="bg-action px-6 py-4 flex justify-between items-center text-white rounded-t-2xl">
                    <h3 className="font-bold flex items-center gap-2 text-lg">
                        <BellIcon className="w-5 h-5" />
                        {isGroup ? 'Group' : 'Push'} Notification to {targetName}
                    </h3>
                    <button onClick={onClose} className="hover:bg-white/20 p-1 rounded-full transition-colors">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    <div className="bg-app-bg rounded-lg p-3 border border-app-border text-xs text-text-muted" style={{ textWrap: 'pretty' }}>
                        Push notifications appear on the participant's device home screen even when they aren't using the app.
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-text-ink uppercase tracking-wider mb-2">Notification Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full border border-app-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-action focus:ring-1 focus:ring-action transition-colors"
                            placeholder="e.g. New Task Assigned"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-text-ink uppercase tracking-wider mb-2">Message Body</label>
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            className="w-full border border-app-border rounded-lg p-3 text-sm focus:outline-none focus:border-action focus:ring-1 focus:ring-action transition-colors min-h-[80px]"
                            placeholder="Write your push notification message..."
                        />
                    </div>

                    {/* Device Preview */}
                    <div className="border border-app-border rounded-xl p-4 bg-neutral-900/5">
                        <p className="text-[10px] font-bold text-text-faint uppercase tracking-widest mb-3">Mobile Device Preview</p>
                        <div className="bg-white/90 backdrop-blur-md rounded-xl shadow-lg p-3 flex items-start gap-3">
                            <Logo className="w-8 h-8" />
                            <div className="min-w-0 flex-1">
                                <div className="flex justify-between items-center">
                                    <p className="text-[13px] font-bold text-black truncate">{title}</p>
                                    <span className="text-[10px] text-text-faint">now</span>
                                </div>
                                <p className="text-[12px] text-text-muted line-clamp-2 leading-tight mt-0.5">{body}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-app-border flex justify-end gap-3 bg-app-bg rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-text-muted hover:bg-app-bg rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={isSending || !title || !body}
                        className="px-6 py-2 text-sm font-medium text-white bg-action hover:bg-action/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg active:scale-95 transition-colors flex items-center gap-2"
                    >
                        {isSending ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <SendIcon className="w-4 h-4" />
                        )}
                        Send Push Notification
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PushNotificationModal;
